import { useEffect, useRef, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';
import type { FormRowObserver } from '../types/block';
import { evaluateCompute } from '../evaluators/computeEvaluator';
import { prefixPath, prefixCompute } from './pathRewriter';
import type { FormPrecisionConfig } from '../precision/precisionContract';
import { normalizeProgrammaticNumericValue } from '../precision/precisionContract';

interface UseRowObserverProcessorParams {
  form: UseFormReturn<Record<string, unknown>>;
  observers: FormRowObserver[];
  rowPrefix: string;
  meta: Record<string, unknown>;
  getOptionProp: (field: string, prop: string) => unknown;
  precisionConfig?: FormPrecisionConfig;
}

export function useRowObserverProcessor({
  form,
  observers,
  rowPrefix,
  meta,
  getOptionProp,
  precisionConfig,
}: UseRowObserverProcessorParams): void {
  const watchFields = useMemo(
    () => observers.map((o) => prefixPath(o.watch, rowPrefix)),
    [observers, rowPrefix],
  );

  const targetFields = useMemo(
    () => observers.map((o) => prefixPath(o.target, rowPrefix)),
    [observers, rowPrefix],
  );

  const watchedValues = useWatch({ control: form.control, name: watchFields });

  // Seed prevValuesRef with the current field values on first render so that
  // mounting a new empty row (all null) does not trigger observers — they
  // should only fire when the user *changes* a watched field after mount.
  const initialWatchMap = useMemo(() => {
    const map: Record<string, unknown> = {};
    watchFields.forEach((field) => {
      map[field] = form.getValues(field as never);
    });
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — only run once on mount

  const prevValuesRef = useRef<Record<string, unknown>>(initialWatchMap);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const userTouchedRef = useRef<Set<string>>(new Set());

  // Detect array structural changes (add/remove) via form.watch subscription.
  // When RHF's useFieldArray.remove() shifts indices, watched values change
  // even though the user didn't edit them. We re-snapshot prevValuesRef so
  // the index shift doesn't trigger observers spuriously.
  const arrayName = useMemo(() => {
    const dotIdx = rowPrefix.indexOf('.');
    return dotIdx > 0 ? rowPrefix.substring(0, dotIdx) : rowPrefix;
  }, [rowPrefix]);
  const prevArrayLengthRef = useRef(
    (() => {
      const arr = form.getValues(arrayName as never);
      return Array.isArray(arr) ? arr.length : 0;
    })(),
  );
  const suppressUntilRef = useRef(0);

  // Use form.watch subscription (not useWatch) to avoid re-rendering this
  // hook on every array item field change. We only care about length changes.
  useEffect(() => {
    const sub = form.watch((formValues, { name }) => {
      if (!name || !name.startsWith(arrayName)) return;
      const arr = formValues[arrayName];
      const currentLen = Array.isArray(arr) ? arr.length : 0;
      const prevLen = prevArrayLengthRef.current;
      if (currentLen !== prevLen) {
        prevArrayLengthRef.current = currentLen;
        // Only suppress on removal (length decrease). Removal shifts indices
        // for all rows at >= removed index, causing spurious "value changed"
        // signals. Adding a row doesn't shift existing rows.
        if (currentLen < prevLen) {
          suppressUntilRef.current = Date.now() + 100;
          const snap: Record<string, unknown> = {};
          watchFields.forEach((field) => {
            snap[field] = form.getValues(field as never);
          });
          prevValuesRef.current = snap;
        }
      }
    });
    return () => sub.unsubscribe();
  }, [form, arrayName, watchFields]);

  useEffect(() => {
    const subs = targetFields
      .filter((_, i) => observers[i].onlyIfDerived)
      .map((targetPath) =>
        form.watch((_, { name, type }) => {
          if (name === targetPath && type === 'change') {
            userTouchedRef.current.add(targetPath);
          }
        }),
      );
    return () => subs.forEach((unsub) => unsub.unsubscribe());
  }, [form, targetFields, observers]);

  useEffect(() => {
    // Skip observer execution during the suppression window after array removal.
    // RHF index-shift causes all watched fields to "change" — this is not user input.
    if (Date.now() < suppressUntilRef.current) {
      const snap: Record<string, unknown> = {};
      watchFields.forEach((field, idx) => {
        snap[field] = Array.isArray(watchedValues) ? watchedValues[idx] : watchedValues;
      });
      prevValuesRef.current = snap;
      return;
    }

    const watchMap: Record<string, unknown> = {};
    watchFields.forEach((field, idx) => {
      watchMap[field] = Array.isArray(watchedValues) ? watchedValues[idx] : watchedValues;
    });

    const currentValues = form.getValues() as Record<string, unknown>;

    for (let i = 0; i < observers.length; i++) {
      const observer = observers[i];
      const watchPath = watchFields[i];
      const value = watchMap[watchPath];

      if (value === prevValuesRef.current[watchPath]) continue;

      const existingTimeout = timeoutsRef.current.get(watchPath);
      if (existingTimeout) clearTimeout(existingTimeout);

      const targetPath = targetFields[i];

      if (observer.onlyIfDerived && userTouchedRef.current.has(targetPath)) continue;

      const debounceMs = observer.debounceMs ?? 0;
      const run = () => {
        const compute = prefixCompute(observer.compute, rowPrefix);
        const computed = evaluateCompute(compute, {
          fieldValue: value,
          values: currentValues,
          meta,
          getOptionProp,
        });
        const normalized = normalizeProgrammaticNumericValue(computed, targetPath, precisionConfig);
        form.setValue(targetPath, normalized, { shouldDirty: false, shouldValidate: false });
      };

      if (debounceMs > 0) {
        const timeout = setTimeout(run, debounceMs);
        timeoutsRef.current.set(watchPath, timeout);
      } else {
        run();
      }
    }

    prevValuesRef.current = { ...watchMap };
  }, [watchedValues, watchFields, targetFields, observers, rowPrefix, form, meta, getOptionProp, precisionConfig]);

  useEffect(() => {
    return () => {
      for (const timeout of timeoutsRef.current.values()) {
        clearTimeout(timeout);
      }
      timeoutsRef.current.clear();
    };
  }, []);
}
